import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getObjectViewedIdFromRoute } from 'utils/menuUtils';

/**
 * Custom hook to get the current object_viewed_id based on the current route
 * @returns {string|null} - The object_viewed_id for the current route, or null if not found
 */
export const useObjectViewedId = () => {
  const location = useLocation();
  const nested_menus = useSelector(state => state.session?.nested_menus);
  
  const object_viewed_id = useMemo(() => {
    return getObjectViewedIdFromRoute(
      nested_menus,
      location.pathname,
      location.state?.from || null
    );
  }, [nested_menus, location.pathname, location.state?.from]);
  
  return object_viewed_id;
};

/**
 * Custom hook to get permissions for the current route
 * @returns {Object|null} - Permission object with rights_add, rights_edit, rights_delete, rights_view, or null
 */
export const useCurrentRoutePermissions = () => {
  const object_viewed_id = useObjectViewedId();
  const user_permissions_raw = useSelector(state => state.session?.user_permissions_raw);
  const current_page_permissions = useSelector(state => state.session?.current_page_permissions);
  
  // If current_page_permissions is already set and has the data, use it
  // Otherwise, try to get from user_permissions_raw using object_viewed_id
  const permissions = useMemo(() => {
    if (current_page_permissions && !Array.isArray(current_page_permissions) && current_page_permissions.object_id) {
      return current_page_permissions;
    }
    
    if (object_viewed_id && user_permissions_raw) {
      // Try to find in user_permissions_raw array
      if (Array.isArray(user_permissions_raw)) {
        const permission = user_permissions_raw.find(
          item => item.object_id === object_viewed_id || item.object_viewed_id === object_viewed_id
        );
        return permission || null;
      } else if (typeof user_permissions_raw === 'object') {
        return user_permissions_raw[object_viewed_id] || null;
      }
    }
    
    return null;
  }, [object_viewed_id, user_permissions_raw, current_page_permissions]);
  
  return permissions;
};
